import React from "react";
import PropTypes from "prop-types";
import { useField } from "formik";
import { Chip, Icon } from "@qgiv/core-react";
import { CardInput } from "@qgiv/core-donor";
import "./ClassificationCard.scss";

const ClassificationCardContent = ({
    description = "",
    displayAddress = false,
    displayEndDate = false,
    displayDate = false,
    displayDivider = false,
    displayMapLink = false,
    displayOnlyIndividualRegistrationAvailable = false,
    displayRegistrantLimit = false,
    displayTeamLimit = false,
    displayTeamLimitReached = false,
    encodedAddress = "",
    formattedAddress = "",
    formattedDate = "",
    formattedOnlyIndividualRegistrationAvailableLabel = "",
    formattedRegistrationEndDateLabel = "",
    formattedRegistrantLimitLabel = "",
    formattedTeamLimitLabel = "",
    formattedTeamLimitReachedLabel = "",
    mapLinkLabel = "",
}) => {
    const mapUrl =
        displayMapLink && encodedAddress && encodedAddress.length > 0
            ? `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
            : "#";

    return (
        // eslint-disable-next-line tailwindcss/classnames-order
        <div className="grid grid--no-gutters classification-card-content">
            {description && description.length && (
                <div className="col col--12">
                    <p className="-type--small">{description}</p>
                </div>
            )}
            {displayDivider && (
                <div className="col col--12">
                    <div className="card__divider" />
                </div>
            )}
            {displayRegistrantLimit && (
                <div className="col col--12">
                    <Chip bgColor="system-warning">
                        <Icon
                            className="-margin-right--10"
                            glyph="user-solid"
                            type="FontAwesome"
                        />
                        <small>{formattedRegistrantLimitLabel}</small>
                    </Chip>
                </div>
            )}
            {displayTeamLimit && (
                <div className="col col--12">
                    <Chip
                        classNames={
                            displayRegistrantLimit ? "-margin-top--10" : ""
                        }
                        bgColor="system-warning"
                    >
                        <Icon
                            className="-margin-right--10"
                            glyph="users-solid"
                            type="FontAwesome"
                        />
                        <small>{formattedTeamLimitLabel}</small>
                    </Chip>
                </div>
            )}
            {displayTeamLimitReached && (
                <div className="col col--12">
                    <Chip classNames="-margin-top--10" bgColor="system-light">
                        <Icon
                            className="-margin-right--10"
                            type="FontAwesome"
                            glyph="exclamation-circle-solid"
                        />
                        <small>{formattedTeamLimitReachedLabel}</small>
                    </Chip>
                </div>
            )}
            {displayOnlyIndividualRegistrationAvailable && (
                <div className="col col--12">
                    <Chip classNames="-margin-top--10" bgColor="system-light">
                        <Icon
                            className="-margin-right--10"
                            type="FontAwesome"
                            glyph="exclamation-circle-solid"
                        />
                        <small>
                            {formattedOnlyIndividualRegistrationAvailableLabel}
                        </small>
                    </Chip>
                </div>
            )}

            {displayEndDate && (
                <div className="col col--12 -margin-top--5">
                    <Icon
                        className="classification-card-content__icon"
                        glyph="clock-regular"
                        type="FontAwesome"
                    />
                    <small>{formattedRegistrationEndDateLabel}</small>
                </div>
            )}
            {displayDate && (
                <div
                    className={`col col--12 ${
                        !displayEndDate && "-margin-top--5"
                    }`}
                >
                    <Icon
                        className="classification-card-content__icon"
                        glyph="calendar-alt-regular"
                        type="FontAwesome"
                    />
                    <small>{formattedDate}</small>
                </div>
            )}
            {displayAddress && (
                <div
                    className={`col col--12 ${
                        !displayEndDate && !displayDate && "-margin-top--5"
                    }`}
                >
                    <Icon
                        className="classification-card-content__icon"
                        glyph="map-marker-alt"
                        type="FontAwesome"
                    />
                    <small>
                        {formattedAddress}
                        {displayMapLink && (
                            <a
                                href={mapUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="-margin-left--10"
                            >
                                {mapLinkLabel}
                            </a>
                        )}
                    </small>
                </div>
            )}
        </div>
    );
};

ClassificationCardContent.propTypes = {
    description: PropTypes.string,
    displayAddress: PropTypes.bool,
    displayEndDate: PropTypes.bool,
    displayDate: PropTypes.bool,
    displayDivider: PropTypes.bool,
    displayMapLink: PropTypes.bool,
    displayOnlyIndividualRegistrationAvailable: PropTypes.bool,
    displayRegistrantLimit: PropTypes.bool,
    displayTeamLimit: PropTypes.bool,
    displayTeamLimitReached: PropTypes.bool,
    encodedAddress: PropTypes.string,
    formattedAddress: PropTypes.string,
    formattedDate: PropTypes.string,
    formattedOnlyIndividualRegistrationAvailableLabel: PropTypes.string,
    formattedRegistrationEndDateLabel: PropTypes.string,
    formattedRegistrantLimitLabel: PropTypes.string,
    formattedTeamLimitLabel: PropTypes.string,
    formattedTeamLimitReachedLabel: PropTypes.string,
    mapLinkLabel: PropTypes.string,
};

const ClassificationCard = ({
    description,
    id,
    name,
    title,
    type = "",
    classificationRef = {},
    ...props
}) => {
    // -------------------------------------------------------------------------
    // NOTE: Make sure to cast value as a string, otherwise formik won't work
    // -------------------------------------------------------------------------
    const [field, , helpers] = useField({
        name,
        type,
        value: `${id}`,
        multiple: true,
    });
    const _props = {
        id,
        title,
        name,
        type,
        handleFocus: () => {
            helpers.setTouched({ value: true, shouldValidate: false });
        },
        handleBlur: field.onBlur,
        handleChange: field.onChange,
        ...field,
        ...props,
    };
    let cardContent;
    if (field.checked) {
        _props.extraTitle = "Change classification";
        // eslint-disable-next-line react/display-name, no-shadow
        _props.renderExtraTitle = (title) => (
            <div className="card__extra-title">
                <span className="-type--large">
                    <Icon glyph="pencil-alt-regular" type="FontAwesome" />
                </span>
                <span className="-visually-hidden">{title}</span>
            </div>
        );
    } else {
        cardContent = (
            <ClassificationCardContent {...props} description={description} />
        );
    }

    return (
        <CardInput {..._props} ref={classificationRef}>
            {cardContent}
        </CardInput>
    );
};

ClassificationCard.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    classificationRef: PropTypes.shape({}),
    type: PropTypes.oneOf(["radio", "checkbox"]),
};

export default ClassificationCard;
